import type { Locale } from './banks.js';

/**
 * Authored table-chatter lines (uplift slice U7): what a character says at a card table when THEY
 * win or lose a trick or a round. Written from the speaker's own view and from public facts only
 * (who took the trick, who scored best/worst) — never a hidden hand. Kind when they lose, never
 * gloating at a child; kept short (< ~70 chars) so they make good TTS clips. Same voice, warmth
 * and tu/tú-form as `banks.ts`; FR/ES are written, not translated word for word.
 */
export type TableEvent = 'trick-win' | 'trick-lose' | 'round-win' | 'round-lose';

export const TABLE_EVENTS: readonly TableEvent[] = ['trick-win', 'trick-lose', 'round-win', 'round-lose'];

type Cell = Readonly<Record<Locale, readonly string[]>>;
type TableBank = Readonly<Record<TableEvent, Cell>>;

export const TABLE_BANKS: Readonly<Record<string, TableBank>> = {
  amara: {
    'trick-win': {
      en: ['Ooh, that one is mine!', 'I’ll take that, thank you!'],
      fr: ['Oh, celui-là est pour moi !', 'Je le prends, merci !'],
      es: ['¡Ay, esta baza es mía!', '¡Me la quedo, gracias!'],
    },
    'trick-lose': {
      en: ['Nice one! You got that trick.', 'Oh, good play!'],
      fr: ['Joli coup ! Le pli est à toi.', 'Oh, bien joué !'],
      es: ['¡Bien! Esa baza es tuya.', '¡Qué buena jugada!'],
    },
    'round-win': {
      en: ['What a round! That was fun.', 'Yay, a good round for me!'],
      fr: ['Quelle manche ! C’était amusant.', 'Youpi, une belle manche pour moi !'],
      es: ['¡Qué ronda! Fue muy divertida.', '¡Yupi, buena ronda para mí!'],
    },
    'round-lose': {
      en: ['Whoops, not my round. Next one!', 'That was tough, but fun!'],
      fr: ['Oups, pas ma manche. La suivante !', 'C’était dur, mais amusant !'],
      es: ['Ups, esta ronda no fue mía. ¡La próxima!', 'Fue difícil, ¡pero divertido!'],
    },
  },
  mateo: {
    'trick-win': {
      en: ['Mine! All mine! …that one, anyway.', 'Ha! Did I plan that? Yes. Maybe.'],
      fr: ['À moi ! Tout à moi ! … ce pli, en tout cas.', 'Ha ! Je l’avais prévu ? Oui. Peut-être.'],
      es: ['¡Mía! ¡Toda mía! …esa baza, al menos.', '¡Ja! ¿Lo planeé? Sí. Quizá.'],
    },
    'trick-lose': {
      en: ['Oof, you got me on that one!', 'Wow, okay, nice move!'],
      fr: ['Aïe, tu m’as eu sur celui-là !', 'Wow, bon, beau coup !'],
      es: ['¡Uf, me ganaste esa!', '¡Guau, vale, buena jugada!'],
    },
    'round-win': {
      en: ['That round? Awesome. I can’t believe it!', 'Best round ever! …for me, today.'],
      fr: ['Cette manche ? Géniale. J’y crois pas !', 'La meilleure manche ! … pour moi, aujourd’hui.'],
      es: ['¿Esa ronda? Genial. ¡No me lo creo!', '¡La mejor ronda! …para mí, hoy.'],
    },
    'round-lose': {
      en: ['Ouch! That round got away from me.', 'Okay, that went badly. Funny, though!'],
      fr: ['Aïe ! Cette manche m’a échappé.', 'Bon, ça s’est mal passé. Drôle, quand même !'],
      es: ['¡Auch! Esa ronda se me escapó.', 'Vale, salió mal. ¡Pero fue gracioso!'],
    },
  },
  layla: {
    'trick-win': {
      en: ['Ah, that worked out nicely.', 'I think that trick is mine.'],
      fr: ['Ah, ça s’est bien passé.', 'Je crois que ce pli est à moi.'],
      es: ['Ah, qué bien salió.', 'Creo que esta baza es mía.'],
    },
    'trick-lose': {
      en: ['Oh, clever! I didn’t see that.', 'Good one. I’ll remember it.'],
      fr: ['Oh, malin ! Je n’avais pas vu.', 'Bien joué. Je m’en souviendrai.'],
      es: ['¡Qué lista! No lo vi venir.', 'Buena jugada. Me acordaré.'],
    },
    'round-win': {
      en: ['A lovely round. I learned so much!', 'That went well. Thank you for playing!'],
      fr: ['Une belle manche. J’ai tant appris !', 'Ça s’est bien passé. Merci de jouer !'],
      es: ['Una ronda preciosa. ¡Aprendí mucho!', 'Salió bien. ¡Gracias por jugar!'],
    },
    'round-lose': {
      en: ['Hmm, I’ll do better next round.', 'I learned something there, at least.'],
      fr: ['Hmm, je ferai mieux à la prochaine.', 'J’ai appris quelque chose, au moins.'],
      es: ['Mmm, en la próxima lo haré mejor.', 'Al menos aprendí algo.'],
    },
  },
  kenji: {
    'trick-win': {
      en: ['As expected.', 'That was part of the plan.'],
      fr: ['Comme prévu.', 'Ça faisait partie du plan.'],
      es: ['Como esperaba.', 'Eso era parte del plan.'],
    },
    'trick-lose': {
      en: ['Hm. Good move. I missed that.', 'You got that one. Well played.'],
      fr: ['Hm. Beau coup. Je l’ai raté.', 'Celui-là est à toi. Bien joué.'],
      es: ['Mm. Buena jugada. No la vi.', 'Esa es tuya. Bien jugado.'],
    },
    'round-win': {
      en: ['A good round. The plan worked.', 'Not bad. I’ll take that round.'],
      fr: ['Une bonne manche. Le plan a marché.', 'Pas mal. Je prends cette manche.'],
      es: ['Buena ronda. El plan funcionó.', 'Nada mal. Me quedo esta ronda.'],
    },
    'round-lose': {
      en: ['A bad round. I need a new plan.', 'You were better that round. Respect.'],
      fr: ['Mauvaise manche. Il me faut un autre plan.', 'Tu as été meilleur. Respect.'],
      es: ['Mala ronda. Necesito otro plan.', 'Fuiste mejor esa ronda. Respeto.'],
    },
  },
  priya: {
    'trick-win': {
      en: ['Safe and sound. That one is mine.', 'Nice and steady. I’ll take it.'],
      fr: ['Tranquille. Celui-là est à moi.', 'Sans se presser. Je le prends.'],
      es: ['Con calma. Esta baza es mía.', 'Sin prisa. Me la llevo.'],
    },
    'trick-lose': {
      en: ['Ah, you took that one. Well done!', 'That was brave of you!'],
      fr: ['Ah, tu l’as pris. Bravo !', 'C’était courageux de ta part !'],
      es: ['Ah, te la llevaste. ¡Bien hecho!', '¡Qué valiente!'],
    },
    'round-win': {
      en: ['Slow and steady worked this round.', 'A calm round. I liked it.'],
      fr: ['Doucement mais sûrement, ça a marché.', 'Une manche tranquille. J’ai aimé.'],
      es: ['Paso a paso funcionó esta ronda.', 'Una ronda tranquila. Me gustó.'],
    },
    'round-lose': {
      en: ['That round was a little scary.', 'Oh dear. I’ll be more careful.'],
      fr: ['Cette manche faisait un peu peur.', 'Oh là là. Je ferai plus attention.'],
      es: ['Esa ronda dio un poco de miedo.', 'Vaya. Tendré más cuidado.'],
    },
  },
  oliver: {
    'trick-win': {
      en: ['YES! Plan number two worked!', 'Ooh, I got it! Wait, did I?'],
      fr: ['OUI ! Le plan numéro deux a marché !', 'Oh, je l’ai ! Attends, c’est vrai ?'],
      es: ['¡SÍ! ¡El plan número dos funcionó!', '¡Oh, la tengo! Espera, ¿es verdad?'],
    },
    'trick-lose': {
      en: ['Aww, you got it! Good job!', 'Oops! That was my plan too!'],
      fr: ['Oh, c’est toi ! Bravo !', 'Oups ! C’était aussi mon plan !'],
      es: ['¡Oh, te la llevaste! ¡Buen trabajo!', '¡Ups! ¡Ese también era mi plan!'],
    },
    'round-win': {
      en: ['A whole round! My plans worked!', 'Hooray! That round was great!'],
      fr: ['Une manche entière ! Mes plans ont marché !', 'Hourra ! Quelle belle manche !'],
      es: ['¡Una ronda entera! ¡Mis planes funcionaron!', '¡Hurra! ¡Qué gran ronda!'],
    },
    'round-lose': {
      en: ['That round was a mess. A fun mess!', 'Okay, new plans for next round!'],
      fr: ['Cette manche, quel désordre. Un joli désordre !', 'Bon, de nouveaux plans pour la suivante !'],
      es: ['Esa ronda fue un lío. ¡Un lío divertido!', '¡Vale, planes nuevos para la próxima!'],
    },
  },
  marisol: {
    'trick-win': {
      en: ['That trick is mine. Good.', 'Careful play pays off.'],
      fr: ['Ce pli est à moi. Bien.', 'Un jeu soigné paie toujours.'],
      es: ['Esa baza es mía. Bien.', 'Jugar con cuidado da resultado.'],
    },
    'trick-lose': {
      en: ['Well taken. That was good play.', 'You earned that trick.'],
      fr: ['Bien pris. C’était bien joué.', 'Tu as mérité ce pli.'],
      es: ['Bien ganada. Fue una buena jugada.', 'Te mereces esa baza.'],
    },
    'round-win': {
      en: ['A solid round. Thank you.', 'Good round. We both played well.'],
      fr: ['Une manche solide. Merci.', 'Bonne manche. Nous avons bien joué.'],
      es: ['Una ronda sólida. Gracias.', 'Buena ronda. Jugamos bien las dos.'],
    },
    'round-lose': {
      en: ['A hard round. I will learn from it.', 'You beat me that round. Well done.'],
      fr: ['Une manche difficile. J’en tirerai une leçon.', 'Tu m’as battue cette manche. Bravo.'],
      es: ['Una ronda dura. Aprenderé de ella.', 'Me ganaste esa ronda. Bien hecho.'],
    },
  },
  kofi: {
    'trick-win': {
      en: ['Ah, an old trick for an old man.', 'Slowly, slowly. That one is mine.'],
      fr: ['Ah, une vieille ruse pour un vieil homme.', 'Doucement, doucement. Celui-là est à moi.'],
      es: ['Ah, una vieja treta para un viejo.', 'Despacito. Esa es mía.'],
    },
    'trick-lose': {
      en: ['Well taken, little one!', 'Ha! You have sharp eyes.'],
      fr: ['Bien pris, petit !', 'Ha ! Tu as de bons yeux.'],
      es: ['¡Bien ganada, pequeño!', '¡Ja! Tienes buen ojo.'],
    },
    'round-win': {
      en: ['A fine round. Thank you, friend.', 'Patience wins a round or two.'],
      fr: ['Une belle manche. Merci, mon ami.', 'La patience gagne une manche ou deux.'],
      es: ['Una buena ronda. Gracias, amigo.', 'La paciencia gana una ronda o dos.'],
    },
    'round-lose': {
      en: ['Not my round. There is always another.', 'You played it better. Bravo, little one.'],
      fr: ['Pas ma manche. Il y en a toujours une autre.', 'Tu as mieux joué. Bravo, petit.'],
      es: ['No fue mi ronda. Siempre hay otra.', 'Jugaste mejor. Bravo, pequeño.'],
    },
  },
  karim: {
    'trick-win': {
      en: ['Mine. Stay calm and it comes.', 'That trick went my way.'],
      fr: ['À moi. Reste calme et ça vient.', 'Ce pli a tourné pour moi.'],
      es: ['Mía. Con calma, todo llega.', 'Esa baza salió a mi favor.'],
    },
    'trick-lose': {
      en: ['Nice. I didn’t see that coming.', 'Okay, that one was yours.'],
      fr: ['Joli. Je ne l’ai pas vu venir.', 'D’accord, celui-là était à toi.'],
      es: ['Bien. No lo vi venir.', 'Vale, esa fue tuya.'],
    },
    'round-win': {
      en: ['Good round. Calm and clean.', 'That round went well. Thanks.'],
      fr: ['Bonne manche. Calme et propre.', 'Cette manche s’est bien passée. Merci.'],
      es: ['Buena ronda. Calma y limpieza.', 'Esa ronda salió bien. Gracias.'],
    },
    'round-lose': {
      en: ['Rough round. I’ll reset.', 'You read that round better than me.'],
      fr: ['Manche difficile. Je recommence à zéro.', 'Tu as mieux lu cette manche que moi.'],
      es: ['Ronda dura. Me reinicio.', 'Leíste mejor que yo esa ronda.'],
    },
  },
  hannah: {
    'trick-win': {
      en: ['I thought first, and it worked!', 'Yes, I counted right. That’s mine!'],
      fr: ['J’ai réfléchi d’abord, et ça a marché !', 'Oui, j’ai bien compté. Il est à moi !'],
      es: ['¡Pensé primero y funcionó!', '¡Sí, conté bien! ¡Es mía!'],
    },
    'trick-lose': {
      en: ['Ooh, you took it! Smart!', 'I didn’t count that one. Well done!'],
      fr: ['Oh, tu l’as pris ! Malin !', 'Je n’avais pas compté celui-là. Bravo !'],
      es: ['¡Oh, te la llevaste! ¡Lista!', 'Esa no la había contado. ¡Bien hecho!'],
    },
    'round-win': {
      en: ['A careful round paid off!', 'Good round! Thinking first works.'],
      fr: ['Une manche prudente, ça a payé !', 'Bonne manche ! Réfléchir d’abord, ça marche.'],
      es: ['¡Una ronda con cuidado dio resultado!', '¡Buena ronda! Pensar primero funciona.'],
    },
    'round-lose': {
      en: ['Oh no, I should have counted more.', 'That round was hard. I’ll try again!'],
      fr: ['Oh non, j’aurais dû mieux compter.', 'Cette manche était dure. J’essaie encore !'],
      es: ['Ay, debí contar mejor.', 'Esa ronda fue difícil. ¡Lo intento otra vez!'],
    },
  },
  wei: {
    'trick-win': {
      en: ['That one is mine. Fair and square.', 'Good, good. I’ll take that.'],
      fr: ['Celui-là est à moi. Loyalement.', 'Bien, bien. Je le prends.'],
      es: ['Esta es mía. Limpiamente.', 'Bien, bien. Me la quedo.'],
    },
    'trick-lose': {
      en: ['Nicely done! You earned that one.', 'Ha, well played!'],
      fr: ['Joliment joué ! Tu l’as mérité.', 'Ha, bien joué !'],
      es: ['¡Muy bien! Te lo has ganado.', '¡Ja, bien jugado!'],
    },
    'round-win': {
      en: ['A good round. Thank you for it.', 'That went well. Let’s keep going!'],
      fr: ['Une bonne manche. Merci pour elle.', 'Ça s’est bien passé. Continuons !'],
      es: ['Una buena ronda. Gracias por ella.', 'Salió bien. ¡Sigamos!'],
    },
    'round-lose': {
      en: ['That round was yours. Well earned.', 'A tough round. We learn and go on.'],
      fr: ['Cette manche était à toi. Bien mérité.', 'Manche difficile. On apprend et on continue.'],
      es: ['Esa ronda fue tuya. Bien ganada.', 'Ronda dura. Aprendemos y seguimos.'],
    },
  },
  fatima: {
    'trick-win': {
      en: ['Oh ho, that one is Grandma’s!', 'Sweet little trick. Thank you, dear.'],
      fr: ['Oh là, celui-là est à Mamie !', 'Un joli petit pli. Merci, mon chéri.'],
      es: ['¡Oh, esa es de la abuela!', 'Qué bonita baza. Gracias, cariño.'],
    },
    'trick-lose': {
      en: ['Marvelous, dear! You took it.', 'Oh, well played! I clap for you.'],
      fr: ['Merveilleux, mon chéri ! Tu l’as pris.', 'Oh, bien joué ! Je t’applaudis.'],
      es: ['¡Maravilloso, cariño! Te la llevaste.', '¡Oh, bien jugado! Te aplaudo.'],
    },
    'round-win': {
      en: ['A lovely round. Grandma is happy.', 'Wonderful! What a sweet round.'],
      fr: ['Une belle manche. Mamie est contente.', 'Merveilleux ! Quelle douce manche.'],
      es: ['Una ronda preciosa. La abuela está feliz.', '¡Maravilloso! Qué ronda tan dulce.'],
    },
    'round-lose': {
      en: ['Never mind, dear. Grandma tries again.', 'You were better! I am proud of you.'],
      fr: ['Ce n’est rien, mon chéri. Mamie réessaie.', 'Tu as été meilleur ! Je suis fière de toi.'],
      es: ['No importa, cariño. La abuela lo intenta de nuevo.', '¡Fuiste mejor! Estoy orgullosa de ti.'],
    },
  },
};
